/**
 * Fullscreen zoom carousel dialog for the product gallery.
 *
 * Adaption of the Horizon theme zoom dialog (`zoom-dialog.js` + `drag-zoom-wrapper.js`)
 * to run as a self-contained classic (non-module) script, without the @theme/* runtime.
 *
 * It only activates on `media-gallery[data-zoom-enabled]` galleries (rendered by the
 * `product-media-zoom-dialog` snippet through `main-product-default.liquid` when
 * `settings.image_zoom == 'lightbox_carousel'`).
 *
 * Clicks on image slides (`[data-zoom-media]`) are intercepted in the capture phase
 * before `<modal-opener>` can open the classic Dawn modal, and the fullscreen
 * carousel dialog is shown instead. Videos / 3D models keep the classic modal.
 */
(function () {
  if (window.__zoomDialogLoaded) return;
  window.__zoomDialogLoaded = true;

  const MOBILE_QUERY = '(max-width: 749px)';
  const DIALOG_CLOSE_EVENT = 'dialog:close';

  const isMobileBreakpoint = () => window.matchMedia(MOBILE_QUERY).matches;
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const preventDefault = (event) => {
    if (typeof event.cancelable !== 'boolean' || event.cancelable) event.preventDefault();
  };
  const debounce = (callback, delay) => {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => callback(...args), delay);
    };
  };

  function preloadImage(url) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    document.head.appendChild(link);
  }

  function scrollThumbnailIntoView(target, container, behavior) {
    if (!target || !container) return;
    const targetRect = target.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const left = container.scrollLeft + targetRect.left - containerRect.left - (containerRect.width - targetRect.width) / 2;
    const top = container.scrollTop + targetRect.top - containerRect.top - (containerRect.height - targetRect.height) / 2;
    container.scrollTo({ left, top, behavior });
  }

  function getMostVisibleElement(elements) {
    return new Promise((resolve) => {
      const observer = new IntersectionObserver(
        (entries) => {
          const mostVisible = entries.reduce((prev, current) =>
            current.intersectionRatio > prev.intersectionRatio ? current : prev
          );
          observer.disconnect();
          resolve(mostVisible.target);
        },
        {
          threshold: Array.from({ length: 100 }, (_, i) => i / 100),
        }
      );

      for (const element of elements) {
        observer.observe(element);
      }
    });
  }

  class ZoomDialog extends HTMLElement {
    #highResImagesLoaded = new Set();

    connectedCallback() {
      this.#dialog = this.querySelector('dialog');
      if (!this.#dialog) return;

      this.#media = Array.from(this.#dialog.querySelectorAll('.dialog-zoomed-gallery > li'));
      const thumbnails = this.#dialog.querySelector('.dialog-thumbnails-list');
      this.#thumbnails = thumbnails;

      this.#dialog.addEventListener('scroll', this.#handleScroll, { passive: true });
      const galleryList = this.#dialog.querySelector('.dialog-zoomed-gallery');
      if (galleryList) galleryList.addEventListener('scroll', this.#handleScroll, { passive: true });

      this.addEventListener('click', (event) => {
        const thumbnail = event.target.closest('button[data-thumbnail-index]');
        if (thumbnail) {
          const index = parseInt(thumbnail.dataset.thumbnailIndex, 10);
          if (this.#dialog.open) this.selectThumbnail(index);
        }
      });

      this.addEventListener('pointerenter', (event) => {
        const thumbnail = event.target.closest('button[data-thumbnail-index]');
        if (thumbnail) {
          const index = parseInt(thumbnail.dataset.thumbnailIndex, 10);
          this.loadHighResolutionImage(this.#media[index]);
        }
      });

      this.#dialog.addEventListener('click', (event) => {
        if (event.target === this.#dialog || event.target.closest('[data-dialog-close]')) {
          this.close();
        }
      });

      this.#dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      this.#dialog.addEventListener('close', () => {
        document.body.classList.remove('overflow-hidden');
      });
    }

    /**
     * Opens the zoom dialog on the given media index.
     * @param {number} index
     */
    open(index) {
      if (!this.#dialog || this.#dialog.open) return;

      const targetImage = this.#media[index];
      if (!targetImage) return;

      this.#dialog.showModal();
      document.body.classList.add('overflow-hidden');

      for (const target of [this.#thumbnails?.children[index], targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }

      this.loadHighResolutionImage(targetImage);
      this.selectThumbnail(index, { behavior: 'instant' });
    }

    /**
     * Closes the zoom dialog.
     */
    close() {
      if (!this.#dialog || !this.#dialog.open) return;
      this.#dialog.close();
      document.body.classList.remove('overflow-hidden');
      window.dispatchEvent(new CustomEvent(DIALOG_CLOSE_EVENT));
    }

    /**
     * Selects the thumbnail matching the given index and scrolls the media into view.
     * @param {number} index
     * @param {Object} options
     * @param {ScrollBehavior} [options.behavior]
     */
    selectThumbnail(index, options = {}) {
      if (!this.#thumbnails || !this.#thumbnails.children.length) return;
      if (Number.isNaN(index) || index < 0 || index >= this.#thumbnails.children.length) return;

      const behavior = options.behavior || (prefersReducedMotion() ? 'instant' : 'smooth');
      const targetThumbnail = this.#thumbnails.children[index];
      const targetImage = this.#media[index];

      Array.from(this.#thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === index}`);
      });

      scrollThumbnailIntoView(targetThumbnail, this.#thumbnails, behavior);

      if (targetImage) {
        targetImage.scrollIntoView({ behavior });
        this.loadHighResolutionImage(targetImage);
      }
    }

    /**
     * Loads a high-resolution version of the image once its dialog slide is visible.
     * @param {HTMLElement} mediaContainer
     */
    loadHighResolutionImage(mediaContainer) {
      if (!mediaContainer || !mediaContainer.classList.contains('product-media-container--image')) return;

      const image = mediaContainer.querySelector('img.product-media__image');
      if (!image || !(image instanceof HTMLImageElement)) return;

      const highResolutionUrl = image.getAttribute('data_max_resolution');
      if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return;

      preloadImage(highResolutionUrl);

      const newImage = new Image();
      newImage.className = image.className;
      newImage.alt = image.alt;
      newImage.setAttribute('data_max_resolution', highResolutionUrl);

      newImage.onload = () => {
        image.replaceWith(newImage);
        this.#highResImagesLoaded.add(highResolutionUrl);
      };

      newImage.src = highResolutionUrl;
    }

    #dialog = null;
    #media = [];
    #thumbnails = null;

    #handleScroll = debounce(async () => {
      if (!this.#dialog?.open) return;

      const mostVisibleElement = await getMostVisibleElement(this.#media);
      const activeIndex = this.#media.indexOf(mostVisibleElement);
      if (activeIndex === -1) return;

      const targetThumbnail = this.#thumbnails?.children[activeIndex];
      if (targetThumbnail instanceof HTMLElement) {
        Array.from(this.#thumbnails.querySelectorAll('button')).forEach((button, i) => {
          button.setAttribute('aria-selected', `${i === activeIndex}`);
        });
      }

      this.loadHighResolutionImage(mostVisibleElement);
    }, 50);
  }

  if (!customElements.get('zoom-dialog')) {
    customElements.define('zoom-dialog', ZoomDialog);
  }

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 5;
  const DEFAULT_ZOOM = 2;
  const DOUBLE_TAP_ZOOM = 2;
  const DOUBLE_TAP_DELAY = 300;
  const DOUBLE_TAP_DISTANCE = 50;
  const DRAG_THRESHOLD = 10;

  function getDistance(point1, point2) {
    const x1 = point1.x ?? point1.clientX;
    const y1 = point1.y ?? point1.clientY;
    const x2 = point2.x ?? point2.clientX;
    const y2 = point2.y ?? point2.clientY;
    const dx = x1 - x2;
    const dy = y1 - y2;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Mobile pinch/drag wrapper, ported from the Horizon theme.
   */
  class DragZoomWrapper extends HTMLElement {
    #controller = new AbortController();
    #scale = DEFAULT_ZOOM;
    #initialDistance = 0;
    #startScale = DEFAULT_ZOOM;
    #translate = { x: 0, y: 0 };
    #startPosition = { x: 0, y: 0 };
    #startTranslate = { x: 0, y: 0 };
    #isDragging = false;
    #initialized = false;
    #animationFrame = null;
    #lastTapTime = 0;
    #lastTapPosition = null;
    #hasDraggedBeyondThreshold = false;
    #hasManualZoom = false;

    get #image() {
      return this.querySelector('img');
    }

    connectedCallback() {
      if (!this.#image) return;

      this.#resizeObserver.observe(this);
      window.addEventListener(DIALOG_CLOSE_EVENT, this.#resetZoom);

      if (!isMobileBreakpoint()) return;

      this.#initEventListeners();
      this.#updateTransform();
    }

    disconnectedCallback() {
      window.removeEventListener(DIALOG_CLOSE_EVENT, this.#resetZoom);
      this.#controller.abort();
      this.#resizeObserver.disconnect();
      this.#cancelAnimationFrame();
    }

    #initEventListeners() {
      if (this.#initialized) return;
      this.#initialized = true;
      const { signal } = this.#controller;
      const options = { passive: false, signal };

      this.addEventListener('touchstart', this.#handleTouchStart, options);
      this.addEventListener('touchmove', this.#handleTouchMove, options);
      this.addEventListener('touchend', this.#handleTouchEnd, options);

      this.#updateTransform();
    }

    #handleResize = () => {
      if (!this.#initialized && isMobileBreakpoint()) {
        this.#initEventListeners();
      }

      if (this.#initialized) {
        this.#requestUpdateTransform();
      }
    };

    #resizeObserver = new ResizeObserver(this.#handleResize);

    #handleTouchStart = (event) => {
      preventDefault(event);

      const touchCount = event.touches.length;

      if (touchCount === 2) {
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        if (!touch1 || !touch2) return;

        this.#startZoomGestureFromTouches(touch1, touch2);
      } else if (touchCount === 1) {
        const touch = event.touches[0];
        if (!touch) return;

        const currentTime = performance.now();
        const timeSinceLastTap = currentTime - this.#lastTapTime;

        if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
          this.#storeTapInfo(currentTime, touch);
          this.#startDragGestureFromTouch(touch);
          return;
        }

        if (this.#lastTapPosition) {
          const distance = getDistance(touch, this.#lastTapPosition);

          if (distance < DOUBLE_TAP_DISTANCE) {
            this.#handleDoubleTapFromTouch(touch);
            this.#lastTapTime = 0;
            this.#lastTapPosition = null;
            return;
          }
        }

        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
      }
    };

    #startZoomGestureFromTouches(touch1, touch2) {
      this.#initialDistance = getDistance(touch1, touch2);
      this.#startScale = this.#scale;
      this.#isDragging = false;
    }

    #startDragGestureFromTouch(touch) {
      this.#startPosition = { x: touch.clientX, y: touch.clientY };
      this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
      this.#isDragging = true;
      this.#hasDraggedBeyondThreshold = false;
    }

    #storeTapInfo(currentTime, touch) {
      this.#lastTapTime = currentTime;
      this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
    }

    #handleDoubleTapFromTouch(touch) {
      const containerCenter = {
        x: this.clientWidth / 2,
        y: this.clientHeight / 2,
      };

      let targetZoom;

      if (this.#hasManualZoom) {
        targetZoom = MIN_ZOOM;
        this.#hasManualZoom = false;
        this.#translate = { x: 0, y: 0 };
      } else {
        const tolerance = 0.05;

        if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
          targetZoom = DOUBLE_TAP_ZOOM;
        } else {
          targetZoom = MIN_ZOOM;
        }
      }

      if (targetZoom !== MIN_ZOOM) {
        const oldScale = this.#scale;
        this.#scale = Math.min(MAX_ZOOM, targetZoom);

        const distanceFromCenter = {
          x: touch.clientX - containerCenter.x,
          y: touch.clientY - containerCenter.y,
        };

        const scaleDelta = this.#scale / oldScale - 1.0;
        this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
        this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
      } else {
        this.#scale = targetZoom;
        this.#translate = { x: 0, y: 0 };
      }

      this.#requestUpdateTransform();
    }

    #handleTouchMove = (event) => {
      preventDefault(event);

      const touchCount = event.touches.length;

      if (touchCount === 2) {
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        if (touch1 && touch2) {
          this.#processZoomGesture(touch1, touch2);
        }
      } else if (touchCount === 1 && this.#isDragging) {
        const touch = event.touches[0];
        if (touch) {
          this.#processDragGesture(touch);
        }
      }
    };

    #processZoomGesture(touch1, touch2) {
      const midX = (touch1.clientX + touch2.clientX) / 2;
      const midY = (touch1.clientY + touch2.clientY) / 2;

      const currentDistance = getDistance(touch1, touch2);

      const oldScale = this.#scale;
      const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
      this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

      this.#hasManualZoom = true;

      const containerCenterX = this.clientWidth / 2;
      const containerCenterY = this.clientHeight / 2;

      const distanceFromCenterX = midX - containerCenterX;
      const distanceFromCenterY = midY - containerCenterY;

      const scaleDelta = this.#scale / oldScale - 1.0;

      this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

      this.#requestUpdateTransform();
      this.#isDragging = false;
    }

    #processDragGesture(touch) {
      const distance = getDistance(touch, this.#startPosition);

      if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
        return;
      }

      this.#hasDraggedBeyondThreshold = true;

      const dx = touch.clientX - this.#startPosition.x;
      const dy = touch.clientY - this.#startPosition.y;

      this.#translate.x = this.#startTranslate.x + dx / this.#scale;
      this.#translate.y = this.#startTranslate.y + dy / this.#scale;

      this.#requestUpdateTransform();
    }

    #handleTouchEnd = (event) => {
      if (event.touches.length === 0) {
        this.#isDragging = false;
        this.#requestUpdateTransform();
        this.#hasDraggedBeyondThreshold = false;
      }
    };

    #constrainTranslation() {
      const containerWidth = this.clientWidth;
      const containerHeight = this.clientHeight;
      if (!containerWidth || !containerHeight || !this.#image) return;

      this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

      if (this.#scale <= MIN_ZOOM) {
        this.#translate.x = 0;
        this.#translate.y = 0;
        return;
      }

      const wrapperRect = this.getBoundingClientRect();

      const imageElement = this.#image;
      let naturalWidth;
      let naturalHeight;

      if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
        naturalWidth = imageElement.naturalWidth;
        naturalHeight = imageElement.naturalHeight;
      } else {
        naturalWidth = wrapperRect.width;
        naturalHeight = wrapperRect.width;
      }

      const imageAspectRatio = naturalWidth / naturalHeight;
      const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

      let actualImageWidth;
      let actualImageHeight;

      if (imageAspectRatio > wrapperAspectRatio) {
        actualImageWidth = wrapperRect.width;
        actualImageHeight = actualImageWidth / imageAspectRatio;
      } else {
        actualImageHeight = wrapperRect.height;
        actualImageWidth = actualImageHeight * imageAspectRatio;
      }

      const scaledImageWidth = actualImageWidth * this.#scale;
      const scaledImageHeight = actualImageHeight * this.#scale;

      const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
      const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

      const maxTranslateX = horizontalOverflow / 2 / this.#scale;
      const maxTranslateY = verticalOverflow / 2 / this.#scale;

      this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
      this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
    }

    #requestUpdateTransform = () => {
      if (!this.#animationFrame) {
        this.#animationFrame = requestAnimationFrame(this.#updateTransform);
      }
    };

    #cancelAnimationFrame() {
      if (this.#animationFrame) {
        cancelAnimationFrame(this.#animationFrame);
        this.#animationFrame = null;
      }
    }

    #updateTransform = () => {
      this.#animationFrame = null;

      this.#constrainTranslation();
      this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
      this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
      this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    };

    #resetZoom = () => {
      this.#scale = DEFAULT_ZOOM;
      this.#startScale = DEFAULT_ZOOM;
      this.#translate.x = 0;
      this.#translate.y = 0;
      this.#startPosition = { x: 0, y: 0 };
      this.#startTranslate = { x: 0, y: 0 };
      this.#isDragging = false;
      this.#lastTapTime = 0;
      this.#lastTapPosition = null;
      this.#hasDraggedBeyondThreshold = false;
      this.#hasManualZoom = false;

      this.style.setProperty('--drag-zoom-scale', DEFAULT_ZOOM.toString());
      this.style.setProperty('--drag-zoom-translate-x', '0px');
      this.style.setProperty('--drag-zoom-translate-y', '0px');
    };
  }

  if (!customElements.get('drag-zoom-wrapper')) {
    customElements.define('drag-zoom-wrapper', DragZoomWrapper);
  }

  /**
   * Opens the zoom dialog when an image slide is clicked, instead of the classic modal.
   */
  function initGalleryClickHandler() {
    document.addEventListener(
      'click',
      (event) => {
        const slide = event.target.closest('media-gallery[data-zoom-enabled] [data-zoom-media]');
        if (!slide) return;

        const gallery = slide.closest('media-gallery[data-zoom-enabled]');
        const zoomDialog = gallery?.querySelector('zoom-dialog');
        if (!zoomDialog || typeof zoomDialog.open !== 'function') return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const mediaId = String(slide.dataset.mediaId || '').split('-').pop();
        const items = Array.from(zoomDialog.querySelectorAll('.dialog-zoomed-gallery > li'));
        const index = items.findIndex((item) => String(item.dataset.mediaId) === mediaId);
        if (index > -1) zoomDialog.open(index);
      },
      true
    );
  }

  initGalleryClickHandler();
})();
